import { Link, Navigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "../auth/AuthContext";
import { AgentLayout } from "./AgentLayout";
import { Layout } from "./Layout";
import { AgentPanelPage } from "../pages/AgentPanelPage";

/**
 * Route guard is defence in depth, not the defence: the API itself 403s an
 * agent token on every operator endpoint (`get_current_user`'s fail-closed
 * gate, `app/deps/auth.py`), and 404s an agent trying another seat
 * (`_require_own_extension`). It exists so a signed-in operator never SEES a
 * screen that would then fail underneath them — a page full of 403 error
 * toasts reads as a broken product, not as "you're not allowed here".
 *
 * There is no equivalent standalone `AgentRoute`: `/agent` is the only
 * agent-reachable route, and it must ALSO stay reachable by operators (the
 * backward-compatible shared-login flow) with different chrome — a guard
 * whose job is "exclude everyone but an agent" can't express that dual
 * audience, so `AgentPanelRoute` below implements that one route's bespoke
 * guard-and-chrome logic directly rather than composing it from a reusable
 * "agent-only" primitive that would sit unused everywhere else.
 */

/** Operator-only. An agent lands on their console instead of a 403 storm. */
export function OperatorRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.principal === "agent") return <Navigate to="/agent" replace />;
  return <>{children}</>;
}

/**
 * Shown instead of a silent redirect when an agent's OWN session ends for
 * real (see AuthContext's `sessionEndedPrincipal`). Previously, any 401 from
 * ANY tab wiped the shared token, so a stale tab still polling with a dead
 * token could — and did, in production — silently sign out a DIFFERENT tab
 * that had just authenticated successfully; the console would just vanish
 * with no explanation (beads_php_master_phone_system-bar). Now that a clear
 * only ever happens for a genuine invalidation of THIS session, this message
 * is honest: it only appears when the agent really does need to sign back
 * in, and it tells them so instead of leaving them staring at a dead screen.
 */
export function AgentSessionEnded() {
  return (
    <div className="max-w-sm mx-auto mt-10 bg-white rounded-lg shadow p-8 text-center space-y-4">
      <h1 className="text-xl font-bold">Your session ended</h1>
      <p className="text-sm text-gray-500">Please sign in again to keep taking calls.</p>
      <Link
        to="/agent-login"
        className="inline-block w-full bg-brand hover:bg-brand-dark text-white rounded py-2"
      >
        Sign in
      </Link>
    </div>
  );
}

// `/agent` is reachable by BOTH principals — an operator via the existing
// backward-compatible shared-login flow (Task 3.4's Step 7: AgentPanelPage
// keeps the extension entry box for them), and an agent via their own token.
// It is deliberately ONE route, not two `<Route path="/agent">` entries under
// OperatorRoute/AgentRoute respectively: react-router-dom resolves two routes
// that match the exact same path by JSX/array order, not by which guard would
// currently allow it through — the LATER one is simply unreachable, for every
// visitor, regardless of auth state (verified empirically; this is not
// react-router-dom "supporting" per-principal path ownership). So the choice
// of chrome (full operator Layout+sidebar vs. bare AgentLayout) is made here,
// once, from the same `user.principal` read the two guards use.
export function AgentPanelRoute() {
  const { isAuthenticated, user, sessionEndedPrincipal, sessionEndingWhileOnCall } = useAuth();
  // /agent-login, not /login, for the signed-out case: an agent's JWT is
  // deliberately short-lived (60 min, Resolved Decisions #3 — no refresh
  // tokens, "the console re-prompts on expiry") and /agent is the ONLY page
  // an agent session ever sits on, so a routine mid-shift expiry is the
  // COMMON path through here, not the rare one. Landing them on the
  // OPERATOR /login form would be a dead end: that endpoint never checks
  // agent_user at all, so their real credentials would just 401. An
  // operator's session expiring while parked on /agent (their tokens are
  // NOT short-lived) is the rarer case, and /agent-login carries its own
  // "sign in at the operator portal" link straight back to /login for it —
  // this is why that link exists on AgentLoginPage.
  if (!isAuthenticated) {
    // A detected, real end-of-session for an agent gets an explanation
    // instead of silently bouncing to /agent-login (see AgentSessionEnded's
    // own comment). The operator side is unchanged: straight to
    // /agent-login, exactly as before — sessionEndedPrincipal is only ever
    // "agent" here since that's the only principal this message is for.
    if (sessionEndedPrincipal === "agent") {
      return <AgentSessionEnded />;
    }
    return <Navigate to="/agent-login" replace />;
  }
  if (user?.principal === "agent") {
    return (
      <AgentLayout>
        {/* beads F1: isAuthenticated stays true here for as long as a real
            call is up (AuthContext defers the actual sign-out — see its own
            doc comment) — this branch is unchanged for that whole window,
            which is the point: AgentConsole never unmounts mid-call. This
            banner is the only visible difference, and it disappears on its
            own the instant the call ends and the deferred sign-out finally
            applies (isAuthenticated flips false, landing on the
            AgentSessionEnded branch above). */}
        {sessionEndingWhileOnCall && (
          <div
            role="status"
            className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          >
            Your session ended — you'll be signed out when this call finishes.
          </div>
        )}
        <AgentPanelPage />
      </AgentLayout>
    );
  }
  return (
    <Layout>
      <AgentPanelPage />
    </Layout>
  );
}
